import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import { useRecipient } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { maskAccountNumber } from "byzantine/src/dbbl/utils/masks/accountDestination.mask";
import Address from "../../settings/profile_settings/UserSettingsProfilePage/Address";
import styles from "./RecipientCard.module.scss";

type RecipientCardProps = {
  recipientId: API.RecipientId;
  onDelete: () => void;
};

const RecipientCard = ({ recipientId, onDelete }: RecipientCardProps) => {
  const { l10n } = useLocalization();
  const recipient = useRecipient(recipientId);
  const swiftDestination = recipient?.swift_destination;

  if (!recipient) return null;

  const nameRow = (
    <div className={styles.topRow}>
      <span className={styles.recipientName}>
        {recipient.name}
        {recipient.nickname && (
          <span className={styles.recipientNickname}>
            {" "}
            ({recipient.nickname})
          </span>
        )}
      </span>
      <Button
        kind="plain"
        size="s"
        onClick={onDelete}
        testId="remove-recipient"
      >
        {l10n.getString("button-remove")}
      </Button>
    </div>
  );

  if (!swiftDestination) {
    return (
      <ContentCard kind="bordered" radiusSize="m">
        {nameRow}
        <p className={styles.missingDetails}>
          {l10n.getString("swift-destination-missing")}
        </p>
      </ContentCard>
    );
  }

  const maskedAccount = swiftDestination.account_identifier
    ? `**${maskAccountNumber(swiftDestination.account_identifier)}`
    : "";
  const institutionLine = [swiftDestination.bic, maskedAccount]
    .filter(Boolean)
    .join(" - ");

  return (
    <ContentCard kind="bordered" radiusSize="m">
      {nameRow}
      {swiftDestination.address && (
        <div className={styles.addressSection}>
          <Address address={swiftDestination.address} showCountryCode />
        </div>
      )}
      {institutionLine && (
        <div className={styles.institutionRow}>
          <div className={styles.institutionIcon}>
            <span className="narmi-icon-bank" />
          </div>
          <p>{institutionLine}</p>
        </div>
      )}
    </ContentCard>
  );
};

export default RecipientCard;
